import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { rfqApi } from '../api/endpoints'
import { ApiError } from '../api/types'

/**
 * /rfq/:token — PUBLIC supplier-facing RFQ landing (agentic sourcing).
 *
 * The sourcing agent WhatsApps a supplier a link `…/rfq/<token>`; the token is a
 * SEALED sourcing-thread id (backend functions/src/rfq). This page shows the
 * supplier the stones WE asked THEM about (their own stock) + a download button.
 * The BUYER IS NEVER NAMED — always "a buyer on DiaEx". No sign-in required to
 * view (a logged-out supplier has to reach it), so it renders standalone chrome,
 * not the app header/nav.
 */
type RfqStone = { stockId: string; spec: string; listedPerCarat: number | null }

export function RfqView() {
  const { token = '' } = useParams()
  const [stones, setStones] = useState<RfqStone[]>([])
  const [destination, setDestination] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    rfqApi.get(token)
      .then((r) => {
        if (cancelled) return
        setStones(r.rfq.stones ?? [])
        setDestination(r.rfq.destinationCountry ?? null)
        setError(null)
        setNotFound(false)
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof ApiError && e.status === 404) setNotFound(true)
        else setError('Could not load this request. Please try again shortly.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [token])

  return (
    <div className="min-h-screen bg-cream">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Brand mark — standalone, no app nav. */}
        <div className="mb-8 flex items-center gap-2">
          <span className="font-serif text-lg font-bold tracking-tight text-ink">DiaEx</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">Sourcing</span>
        </div>

        {loading ? (
          <div className="rounded-card border border-border bg-paper p-10 text-center text-sm text-muted shadow-card">
            Loading request…
          </div>
        ) : notFound ? (
          <div className="rounded-card border border-border bg-paper p-10 text-center shadow-card">
            <h1 className="font-serif text-xl font-bold text-ink">Request unavailable</h1>
            <p className="mt-3 text-sm text-muted">This request link has expired or is invalid.</p>
          </div>
        ) : error ? (
          <div className="rounded-card border border-danger/30 bg-danger/5 p-6 text-sm text-danger">{error}</div>
        ) : (
          <div className="rounded-card border border-border bg-paper p-6 sm:p-8 shadow-card">
            <h1 className="font-serif text-2xl font-bold leading-tight tracking-tight text-ink">
              A buyer on DiaEx is interested in your stock
            </h1>
            <p className="mt-2 text-base font-semibold text-ink">Do you have these in stock?</p>

            <ul className="mt-6 divide-y divide-border rounded-card border border-border bg-cream/40 overflow-hidden">
              {stones.length === 0 ? (
                <li className="px-4 py-4 text-sm text-muted">Stone details to follow.</li>
              ) : (
                stones.map((s, i) => (
                  <li key={`${s.stockId}-${i}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-3.5">
                    <span className="font-semibold text-ink">{s.stockId}</span>
                    {s.spec && <span className="text-sm text-muted">— {s.spec}</span>}
                    {s.listedPerCarat != null && (
                      <span className="ml-auto text-[13px] font-medium text-soft tabular-nums">
                        listed ${s.listedPerCarat.toLocaleString('en-US')}/ct
                      </span>
                    )}
                  </li>
                ))
              )}
            </ul>

            {destination && (
              <p className="mt-5 rounded-card border border-gold/30 bg-gold/5 px-4 py-3 text-sm text-ink">
                Buyer&apos;s in {destination} — reply with your shipping cost to there.
              </p>
            )}

            {stones.length > 0 && (
              <button
                type="button"
                onClick={() => downloadRfqCsv(stones)}
                className="mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-navy px-5 text-sm font-semibold text-white shadow-card transition-all hover:bg-navy/90 active:scale-[0.98] sm:w-auto"
                data-testid="rfq-download"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download list
              </button>
            )}
          </div>
        )}

        {!loading && !notFound && !error && (
          <p className="mt-6 text-center text-[13px] text-soft">
            Already a DiaEx supplier?{' '}
            <Link to="/signin" className="font-semibold text-gold hover:underline">Sign in</Link>
          </p>
        )}
      </div>
    </div>
  )
}

/** Build a CSV (Excel-openable) of the requested stones and trigger a download.
 *  Self-contained — no new dependency. Columns: Stock ID, Spec, Listed $/ct. */
function downloadRfqCsv(stones: RfqStone[]): void {
  const headers = ['Stock ID', 'Spec', 'Listed $/ct']
  const cell = (v: unknown) => {
    const s = v == null ? '' : String(v)
    // \r included: a lone carriage return would otherwise escape unquoted and split the row in Excel.
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const rows = stones.map((s) => [s.stockId, s.spec ?? '', s.listedPerCarat ?? ''])
  const csv = [headers, ...rows].map((r) => r.map(cell).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'diaex-rfq.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
